import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMedia } from 'react-use';
import classnames from 'classnames';
import { DividerWithLogo, Button, Icon, LazyLoadImage } from 'ck-ui';
import { getSanityImg } from 'src/sanityClient';
import { HeroWithVideo } from '../Hero/HeroWithVideo';
import { usePromotionView } from '../hooks';
import { getPromoName, trackPromotionClick } from '../utils';
import styles from './ContentBlock.module.scss';

const ContentBlock = ({
  className,
  handle,
  position,
  blockChecklist
}) => {
  const {
    trackingID,
    secondary,
    divider,
    buttonTwoText,
    buttonTwoLink,
    buttonText,
    buttonLink,
    contentList,
    images,
    description,
    btnColour,
    descriptionLarge,
    centerContent,
    reverse,
    videoURL,
    header: title,
    backgroundColour } = blockChecklist;
  const isMobile = useMedia('(max-width: 767px)');
  const imageWidth = isMobile ? 1080 : 600;
  const featureImage = getSanityImg(blockChecklist.featureImage).width(imageWidth).url();
  const multipleImg = blockChecklist.images?.length > 0;
  const [featureImg, setFeatureImg] = useState(featureImage);
  const ref = useRef();
  const promoRef = useRef(null);
  const list = useMemo(() => (contentList ? contentList.split(/\r?\n/g) : []), [contentList]);
  const renderProductImage = (image, index) => (
    <LazyLoadImage
      key={index}
      role="presentation"
      src={image}
      onClick={() => setFeatureImg(image)}
      alt={title}
    />
  );

  // eslint-disable-next-line no-nested-ternary
  const size = !contentList ? (isMobile ? 'medium' : 'exLarge') : (isMobile ? 'large' : 'exLarge');
  const promoBtnOne = {
    id: handle,
    name: getPromoName(handle, trackingID ? `${trackingID} -> ${buttonLink}` : buttonLink),
    creative: featureImage,
    position: `${handle}-${position}-1`
  };

  const promoBtnTwo = {
    id: handle,
    name: getPromoName(handle, trackingID ? `${trackingID} -> ${buttonTwoLink}` : buttonTwoLink),
    creative: featureImage,
    position: `${handle}-${position}-2`
  };

  usePromotionView([
    ...buttonLink ? [promoBtnOne] : [],
    ...buttonTwoLink ? [promoBtnTwo] : []
  ], promoRef);

  useEffect(() => {
    if (isMobile && ref?.current && multipleImg) {
      window.theme.initSlider(ref?.current, { enabledMobileCarousel: true });
    }
  }, [isMobile, multipleImg]);

  return (
    <>
      <div
        className={classnames(styles.container, className)}
        style={{ backgroundColor: backgroundColour }}
        ref={promoRef}
      >
        <div
          className={classnames('container',
            styles.contentWrapper,
            styles.mobileAlignment,
            {
              [styles.reverse]: !reverse,
              [styles.contentDivider]: divider,
              [styles.paddingNoTitle]: !title,
              [styles.paddingTopMobile]: multipleImg
            })}
        >

          {/* Image  */}
          <div className={classnames(styles.imgContainer)}>
            {!multipleImg && featureImage && (
              <LazyLoadImage
                src={featureImage}
                alt={title}
              />
            )}
            {videoURL && (
              <HeroWithVideo
                isFromContentBlock
                handle={handle}
                position={position}
                blockHeroVideo={{
                  desktopVideo: videoURL,
                  mobileVideo: videoURL,
                  trackingID,
                }}
              />
            )}

            {multipleImg && (
              <div className={classnames({ 'is-relative': isMobile })}>
                {!isMobile && (
                  <div className={styles.multiImgContainer}>
                    <div className={styles.multiImgSider}>
                      {renderProductImage(featureImage)}
                      {images.map((img, index) => renderProductImage(getSanityImg(img).width(imageWidth).url(), index))}
                    </div>
                    <div className={classnames(styles.multiImgFeature)}>
                      <LazyLoadImage
                        src={featureImage}
                        alt={title}
                      />
                    </div>
                  </div>
                )}

                {isMobile && (
                  <div ref={ref} className="multi-image-slider is-flex">
                    <LazyLoadImage
                      src={featureImg}
                      alt={title}
                    />

                    {images.map((img, index) => (
                      <LazyLoadImage
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        src={getSanityImg(img).width(imageWidth).url()}
                        alt={title}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className={classnames(styles.contentContainer,
            { [styles.contentPaddingLeftLarge]: reverse && !multipleImg,
              [styles.contentPaddingRightLarge]: !reverse && !multipleImg,
              [styles.contentPaddingLeftSmall]: reverse && multipleImg,
              [styles.contentPaddingRightSmall]: !reverse && multipleImg,
              [styles.marginMobileTop]: !multipleImg && !centerContent
            })}
          >

            <div className={classnames({ [styles.contentMobileTextContainer]: multipleImg })}>

              { title && (
                <h2
                  className={classnames(styles.contentTitle,
                    { [styles.hidden]: !title,
                      [styles.centerText]: centerContent,
                      [styles.textWhite]: backgroundColour,
                      [styles.textBlack]: !backgroundColour }
                  )}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}

              {description && (
                <p
                  className={classnames(styles.contentDescription,
                    { [styles.noMarginTop]: !title,
                      [styles.centerTextMobile]: !title,
                      [styles.centerText]: centerContent,
                      [styles.descriptionLarge]: descriptionLarge,
                      [styles.textGrey]: backgroundColour && centerContent,
                      [styles.textStandard]: !backgroundColour,
                      [styles.textBlack]: multipleImg }
                  )}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
            {list.length > 0 && (
              <ul className={classnames(styles.listContainer)}>
                {list.map((content, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index} className={classnames(styles.listItem)}>
                    <Icon
                      name="circle-tick"
                      className={classnames(styles.circleIcon, { [styles.textWhite]: backgroundColour })}
                      size="medium"
                    />
                    <span
                      className={classnames(styles.listContent,
                        { [styles.textWhite]: backgroundColour })}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  </li>
                ))}
              </ul>
            )}

            <div className={classnames({ [styles.centerBtn]: centerContent })}>
              {buttonTwoLink && (
                <Button
                  renderAs="a"
                  secondary={secondary}
                  color={btnColour || null}
                  href={buttonTwoLink}
                  size={size}
                  onClick={() => trackPromotionClick(promoBtnTwo)}
                > {buttonTwoText}
                </Button>
              )}
              {buttonLink && (
                <Button
                  renderAs="a"
                  secondary={secondary}
                  color={btnColour || null}
                  href={buttonLink}
                  size={size}
                  onClick={() => trackPromotionClick(promoBtnOne)}
                  className={classnames({ [styles.mobileCenterBtn]: contentList })}
                > {buttonText}
                </Button>
              )}
            </div>
          </div>
        </div>

        {divider && (
          <div className={classnames('container', styles.dividerContainer, { [styles.paddingBottomMobile]: multipleImg })}>
            <DividerWithLogo />
          </div>
        )}
      </div>
    </>
  );
};

ContentBlock.propTypes = {
  blockChecklist: PropTypes.object.isRequired,
  className: PropTypes.string,
  handle: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
};

ContentBlock.defaultProps = {
  className: null,
};

export { ContentBlock };
